import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { BrandMark } from '@layerit/ui/components/brand-mark'
import { ThemeMenu } from '@layerit/ui/components/theme-menu'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Field, FieldError, FieldLabel } from '@layerit/ui/components/ui/field'
import { Input } from '@layerit/ui/components/ui/input'
import { OTP_LENGTH, useEmailCode } from '@web/screens/auth/email-code/use-email-code'
import { OtpField } from '@layerit/ui/components/otp-field'
import type { EmailFormValues, OtpFormValues } from '@web/screens/auth/schema'
import { emailFormSchema, otpFormSchema } from '@web/screens/auth/schema'
import { publicSiteHref } from '@web/site'
import { APP_NAME } from '@layerit/brand'

interface VerifyEmailScreenProps {
	kind: 'sign-in' | 'sign-up'
	email: string
	redirectTo: string
	error?: string | null
	navigate: (to: string) => void
	onEmail: (email: string) => void
	backTo: string
	resendSeconds?: number
}

export function VerifyEmailScreen({
	kind,
	email,
	redirectTo,
	error = null,
	navigate,
	onEmail,
	backTo,
	resendSeconds,
}: VerifyEmailScreenProps) {
	const flow = useEmailCode({ email, redirectTo, error, ...(resendSeconds === undefined ? {} : { resendSeconds }) })
	const wordmarkHref = publicSiteHref('/')
	const signingUp = kind === 'sign-up'

	const verifyForm = useForm<OtpFormValues>({
		resolver: zodResolver(otpFormSchema),
		defaultValues: { code: '' },
	})
	const code = useWatch({ control: verifyForm.control, name: 'code' })

	const addressForm = useForm<EmailFormValues>({
		resolver: zodResolver(emailFormSchema),
		defaultValues: { email: '' },
	})
	const address = useWatch({ control: addressForm.control, name: 'email' })

	async function verify({ code: digits }: OtpFormValues) {
		await flow.verify(digits)
	}

	function submitAddress({ email: typed }: EmailFormValues) {
		onEmail(typed)
	}

	const wordmark = (
		<>
			<BrandMark size={28} />
			<span className="text-sm font-semibold tracking-tight text-ink">{APP_NAME}</span>
		</>
	)

	return (
		<div className="flex min-h-screen flex-col bg-surface">
			<div className="flex shrink-0 items-center justify-between gap-4 px-6 py-6">
				{wordmarkHref ? (
					<a className="flex w-fit items-center gap-2.5 rounded-md transition-opacity hover:opacity-80" href={wordmarkHref}>
						{wordmark}
					</a>
				) : (
					<div className="flex w-fit items-center gap-2.5">{wordmark}</div>
				)}
				<ThemeMenu />
			</div>

			<div className="mx-auto flex w-full max-w-104 flex-1 flex-col justify-center px-6 pb-12">
				<div className="flex flex-col">
					<h1 className="mt-6 font-display text-2xl text-ink">Check your email</h1>
					<p className="mt-2.5 text-sm leading-relaxed text-ink-3">
						{email ? (
							<>
								We sent a {OTP_LENGTH}-digit code to <span className="font-medium text-ink-2">{email}</span>. It expires in
								five minutes.
							</>
						) : (
							'Enter the address you used and we will send a new code to it.'
						)}
					</p>

					{flow.failure && (
						<Alert variant="destructive" className="mt-6">
							<AlertDescription>{flow.failure}</AlertDescription>
						</Alert>
					)}

					{email ? (
						<form className="mt-7 flex flex-col" noValidate onSubmit={(event) => void verifyForm.handleSubmit(verify)(event)}>
							<Controller
								control={verifyForm.control}
								name="code"
								render={({ field, fieldState }) => (
									<Field className="gap-2" data-invalid={fieldState.invalid || flow.failure !== null}>
										<FieldLabel htmlFor="code">{signingUp ? 'Verify account code' : 'Sign-in code'}</FieldLabel>
										<OtpField
											ref={field.ref}
											onBlur={field.onBlur}
											value={field.value}
											onChange={field.onChange}
											length={OTP_LENGTH}
											invalid={fieldState.invalid || flow.failure !== null}
											disabled={flow.pending}
										/>
										{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
									</Field>
								)}
							/>

							<div className="mt-3 flex items-center justify-between">
								<Button type="button" variant="quiet" onClick={() => void navigate(backTo)}>
									Use a different email
								</Button>
								<Button
									type="button"
									variant="quiet"
									disabled={flow.sending || flow.cooldown > 0}
									onClick={() => void flow.resend()}
								>
									{flow.sending
										? 'Sending…'
										: flow.cooldown > 0
											? `Send a new code (${flow.cooldown})`
											: 'Send a new code'}
								</Button>
							</div>

							<Button
								type="submit"
								variant="primary"
								className="mt-7 h-11"
								disabled={flow.pending || code.length < OTP_LENGTH}
							>
								{flow.pending ? 'Verifying…' : signingUp ? 'Create account' : 'Verify and continue'}
							</Button>
						</form>
					) : (
						<form
							className="mt-7 flex flex-col"
							noValidate
							onSubmit={(event) => void addressForm.handleSubmit(submitAddress)(event)}
						>
							<Controller
								control={addressForm.control}
								name="email"
								render={({ field, fieldState }) => (
									<Field className="gap-2" data-invalid={fieldState.invalid}>
										<FieldLabel htmlFor="email">Work email</FieldLabel>
										<Input
											{...field}
											id="email"
											type="email"
											autoComplete="email"
											required
											className="h-11"
											aria-invalid={fieldState.invalid}
										/>
										{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
									</Field>
								)}
							/>

							<Button type="submit" variant="primary" className="mt-7 h-11" disabled={address.trim() === ''}>
								Continue
							</Button>
						</form>
					)}
				</div>
			</div>
		</div>
	)
}
